import * as React from 'react'

import { Box, Icon, Tag, Text } from '@factorialco/deprecated-design-system'
import compact from 'lodash/compact'
import countBy from 'lodash/countBy'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import ComputerCharts from 'modules/reports/components/BlankSlate/ComputerChartsMagnifyingGlass.svg'
import favouriteDashboards from 'modules/reports/store/collections/favourite_dashboards'
import reports from 'modules/reports/store/collections/reports'
import type Dashboard from 'modules/reports/store/models/Dashboard'
import Report from 'modules/reports/store/models/Report'

const MAX_TAGS = 3

const i18nTag = (tag: string) => {
  switch (tag) {
    case 'attendance':
      return i18n.t('custom_reports.dashboards.favourites.tags.attendance')

    case 'shift_management':
      return i18n.t('custom_reports.dashboards.favourites.tags.shift_management')

    case 'contracts':
      return i18n.t('custom_reports.dashboards.favourites.tags.contracts')

    case 'documents':
      return i18n.t('custom_reports.dashboards.favourites.tags.documents')

    case 'employees':
      return i18n.t('custom_reports.dashboards.favourites.tags.employees')

    case 'payroll':
      return i18n.t('custom_reports.dashboards.favourites.tags.payroll')

    case 'perfomance':
      return i18n.t('custom_reports.dashboards.favourites.tags.performance')

    case 'recruitment':
      return i18n.t('custom_reports.dashboards.favourites.tags.recruitment')

    case 'timeoff':
      return i18n.t('custom_reports.dashboards.favourites.tags.timeoff')

    default:
      return ''
  }
}

const BlankSlate = () => {
  return (
    <Box flexDirection="column" flexGrow height="full">
      <Box gap="s32" alignItems="center">
        <img
          src={ComputerCharts}
          style={{
            height: '85px',
          }}
        />
        <Box flexDirection="column" alignItems="center" gap="s16" textAlign="center">
          <Text.Normal size="300" weight="medium">
            {i18n.t('custom_reports.dashboards.favourites.blank_slate.title')}
          </Text.Normal>
          <Text.Normal size="200" color="grey800">
            {i18n.t('custom_reports.dashboards.favourites.blank_slate.description')}
          </Text.Normal>
        </Box>
      </Box>
    </Box>
  )
}

interface DetailsProps {
  dashboard: Dashboard
}

const DashboardDetails = ({ dashboard }: DetailsProps) => {
  const items = dashboard.attributes.items || []

  const dashboardReports: Report[] = compact(
    items.map((item: any) =>
      reports.find({
        id: +item.i,
      })
    )
  )

  const counters = countBy(dashboardReports, (item) => {
    return item.get('chart_type')
  })

  const reportsCount = items.length
  const tags: string[] = dashboard.attributes.tags

  const visibleTags = compact(tags.map((tag: string) => i18nTag(tag))).slice(0, MAX_TAGS)

  const favouritedCount = favouriteDashboards.filter({
    dashboard_id: dashboard.id,
  }).length

  return (
    <>
      <Box flexDirection="row" flexGrow gap="s24">
        {Object.entries(counters).map(([name, value]) => (
          <Box key={name} flexDirection="column" alignItems="center" gap="s12">
            <Icon.Large key={name} icon={Report.ICONS[name]!} color="radicalRed1000" />
            <Tag type="grey">{value.toString()}</Tag>
          </Box>
        ))}
      </Box>
      <Box flexDirection="row">
        <Box flexDirection="column" justifyContent="flexEnd" flexGrow width="full">
          {visibleTags.map((tag) => (
            <Box key={tag} flexDirection="row" gap="s8" alignItems="center">
              <Icon.Medium icon="check" />
              <Text.Normal size="100">{tag}</Text.Normal>
            </Box>
          ))}
        </Box>
        <Box flexDirection="column" justifyContent="flexEnd" flexGrow width="full">
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Icon.Medium icon="reports" />
            <Text.Normal size="100">
              {i18n.tp('custom_reports.dashboards.favourites.reports', {
                count: reportsCount,
              })}
            </Text.Normal>
          </Box>
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Icon.Medium icon="star" />
            <Text.Normal size="100">
              {i18n.tp('custom_reports.dashboards.favourites.favourited', {
                count: favouritedCount,
              })}
            </Text.Normal>
          </Box>
        </Box>
      </Box>
    </>
  )
}

interface Props {
  dashboard: Dashboard
}

const Item = ({ dashboard }: Props) => {
  const isEmpty = !(dashboard.attributes.items || []).length
  const [hover, setHover] = React.useState<boolean>(false)

  return (
    <Box paddingY="s8">
      <Box
        paddingX="s40"
        paddingY="s32"
        border={{
          all: {
            width: 's1',
            color: hover ? 'radicalRed1000' : 'grey400',
            style: 'solid',
          },
        }}
        borderRadius={{
          all: 'abs008',
        }}
        background="grey000White"
        width="full"
        gap="s32"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: '400px',
          height: '336px',
        }}
      >
        <Box flexDirection="column" gap="s4" overflowY="hidden" overflowX="hidden">
          <Text.Normal size="300" weight="medium">
            {dashboard.title}
          </Text.Normal>
          {!isEmpty && <Text.Normal size="200">{dashboard.description}</Text.Normal>}
        </Box>

        {isEmpty && <BlankSlate />}
        {!isEmpty && <DashboardDetails dashboard={dashboard} />}
      </Box>
    </Box>
  )
}

export default observer(Item)
